import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Raven from 'raven-js';
import FormattedMessage from 'I18n/components/FormattedMessage';
import H2 from 'UIComponents/elements/headings/H2';
import Small from 'UIComponents/elements/Small';
import FloatingAlertStore from 'UIComponents/alert/FloatingAlertStore';
import UIModalPanel from 'UIComponents/dialog/UIModalPanel';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UIAbstractProgress from 'UIComponents/progress/UIAbstractProgress';
import UINanoProgress from 'UIComponents/progress/UINanoProgress';
import UILink from 'UIComponents/link/UILink';
import UILoadingOverlay from 'UIComponents/loading/UILoadingOverlay';
import UITextArea from 'UIComponents/input/UITextArea';
import UIMediaObject from 'UIComponents/layout/UIMediaObject';
import { AIBadge } from 'ai-components-ui-library/components/informative/AIBadge';
import VoiceSelectWithPreview from 'audio-components/speech/VoiceSelectWithPreview';
import { useFetchContentAssistanceContent } from 'content-assistance-lib/hooks/useFetchContentAssistanceContent';
import { getIsGenerativeAiEnabled } from 'ContentEditorUI/redux/selectors/contentAssistantSelectors';
// @ts-expect-error Untyped module
import withEditorActionsAndEvents from 'ContentEditorUI/components/hoc/withEditorActionsAndEvents';
import { getContentModelId, getLanguage } from 'ContentEditorUI/redux/selectors/baseContentModelSelectors';
import UsageTracker from 'ContentEditorUI/utils/UsageTracker';
import { INPUT_CHAR_LIMIT, Voices } from '../../constants/audioNarration';
import { useFetchFile } from '../../hooks/useFetchFile';
import { acceptNarration, closeAudioNarrationPanel, fetchTranscript, removeNarration, saveTranscript, updateAudioNarrationState } from '../../redux/actions/audioNarrationActions';
import { selectAudioUrl, selectExecutionId, selectHasBlogAudioModuleInTemplate, selectTranscriptText, selectPostBodyForAudioNarration, selectVoice, selectHasFetchedTranscript, selectOpenedVia } from '../../redux/selectors/audioNarrationSelectors';
import { getContentModelTextToAudioFileId } from '../../redux/selectors/postContentModelSelectors';
import AudioPreviewField from './AudioPreviewField';
import AudioPanelFooter from './AudioPanelFooter';
import UnsavedTranscriptChanges from './UnsavedTranscriptChanges';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { View } from 'foundations-components/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { FormLabel } from 'foundations-components/transitional/components/FormLabel';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
let generationStartedAt;
const AudioPreviewPanel = ({
  bus
}) => {
  const dispatch = useDispatch();
  const language = useSelector(getLanguage);
  const objectId = useSelector(getContentModelId);
  const hasBlogAudioModule = useSelector(selectHasBlogAudioModuleInTemplate);
  const textToAudioFileId = useSelector(getContentModelTextToAudioFileId);
  const postBody = useSelector(selectPostBodyForAudioNarration);
  const lastGenerationInputText = useSelector(selectTranscriptText);
  const lastGenerationVoice = useSelector(selectVoice);
  const openedVia = useSelector(selectOpenedVia);
  const isGenerativeAiEnabled = useSelector(getIsGenerativeAiEnabled);
  const [voice, setVoice] = useState(useSelector(selectVoice) || Object.values(Voices)[0]);
  const [audioUrl, setAudioUrl] = useState(useSelector(selectAudioUrl));
  const [executionId, setExecutionId] = useState(useSelector(selectExecutionId));
  const [hasCancelled, setHasCancelled] = useState(false);
  const [isSaveToFilesPending, setIsSaveToFilesPending] = useState(false);
  const [isRemovePending, setIsRemovePending] = useState(false);
  const [inputText, setInputText] = useState(lastGenerationInputText || postBody);
  const [isGenerationPending, setIsGenerationPending] = useState(false);
  const [isUnsavedChangesOpen, setIsUnsavedChangesOpen] = useState(false);
  const [isPanelOpenComplete, setIsPanelOpenComplete] = useState(false);
  const textAreaRef = useRef(undefined);
  const {
    file,
    isLoading: isFileLoading,
    isFailed: isFileFetchFailed
  } = useFetchFile(textToAudioFileId, false);
  const isInputTextEmpty = !inputText.trim();
  const isOverCharLimit = inputText.length > INPUT_CHAR_LIMIT;
  const hasChangedFromPostBody = inputText.trim() !== postBody.trim();
  const hasTranscriptChanged = lastGenerationInputText ? lastGenerationInputText !== inputText : hasChangedFromPostBody;
  const hasVoiceChanged = voice !== lastGenerationVoice;
  useEffect(() => {
    UsageTracker.track('audioNarration', {
      action: textToAudioFileId ? 'panel-opened-with-existing-audio' : 'panel-opened',
      target: openedVia
    });
  }, [textToAudioFileId, openedVia]);
  const {
    fetch
  } = useFetchContentAssistanceContent({
    language: language !== null && language !== void 0 ? language : '',
    basePath: 'content/audio-generation/v1/executions',
    objectId: String(objectId),
    pollingInterval: 1000,
    maxPollingAttempts: 60,
    onSuccess: results => {
      if (hasCancelled) {
        setHasCancelled(false);
        return;
      }
      setIsGenerationPending(false);
      UsageTracker.track('audioNarration', {
        action: 'generate-success',
        count: inputText.length,
        duration: (Date.now() - generationStartedAt) / 1000
      });
      if ('executionId' in results[0] && typeof results[0].executionId === 'string') {
        setExecutionId(results[0].executionId);
        dispatch(updateAudioNarrationState({
          executionId: results[0].executionId
        }));
      }
      if (typeof results[0].value === 'object' && 'url' in results[0].value && typeof results[0].value.url === 'string') {
        setAudioUrl(results[0].value.url);
        dispatch(updateAudioNarrationState({
          audioUrl: results[0].value.url
        }));
      }
    },
    onError: err => {
      console.error('Error generating audio:', err);
      Raven.captureException(err);
      if (hasCancelled) {
        setHasCancelled(false);
        return;
      }
      UsageTracker.track('audioNarration', {
        action: 'generate-failed',
        count: inputText.length,
        duration: (Date.now() - generationStartedAt) / 1000
      });
      setIsGenerationPending(false);
      FloatingAlertStore.addAlert({
        type: 'danger',
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "audioNarration.errors.generic.message"
        })
      });
    }
  });
  const handleClickGenerate = () => {
    setIsGenerationPending(true);
    generationStartedAt = Date.now();
    fetch({
      commandId: 'generate_audio_v1',
      parameters: {
        input: inputText,
        voice
      }
    });
    dispatch(updateAudioNarrationState({
      transcriptText: inputText,
      voice
    }));
    UsageTracker.track('audioNarration', {
      action: audioUrl ? 'regenerate-started' : 'generate-started',
      count: inputText.length,
      style: voice
    });
  };
  const handleAddToPost = async () => {
    setIsSaveToFilesPending(true);
    UsageTracker.track('audioNarration', {
      action: textToAudioFileId ? 'update-audio' : 'add-to-post'
    });
    try {
      await dispatch(acceptNarration(objectId, executionId));
      await dispatch(saveTranscript(objectId, inputText, voice));
      UsageTracker.track('audioNarration', {
        action: 'save-to-post-success'
      });
      bus.trigger('preview:refresh');
      dispatch(closeAudioNarrationPanel);
      setIsSaveToFilesPending(false);
    } catch (err) {
      FloatingAlertStore.addAlert({
        type: 'danger',
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "audioNarration.errors.accept.message"
        })
      });
      UsageTracker.track('audioNarration', {
        action: 'save-to-post-failed'
      });
      setIsSaveToFilesPending(false);
    }
  };
  const handleRemove = async () => {
    setIsRemovePending(true);
    try {
      await dispatch(removeNarration());
    } catch (err) {
      FloatingAlertStore.addAlert({
        type: 'danger',
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "audioNarration.errors.remove.message"
        })
      });
    }
    dispatch(closeAudioNarrationPanel);
    setIsRemovePending(false);
    bus.trigger('preview:refresh');
    UsageTracker.track('audioNarration', {
      action: 'remove-audio'
    });
  };
  const handleReset = () => {
    setInputText(postBody);
    setTimeout(() => {
      if (textAreaRef.current) {
        textAreaRef.current.scrollTo(0, 0);
      }
    });
    UsageTracker.track('audioNarration', {
      action: 'reset-transcript'
    });
  };
  const handleChangeVoice = ({
    target: {
      value: selectedVoice
    }
  }) => {
    setVoice(selectedVoice);
    UsageTracker.track('audioNarration', {
      action: 'select-voice',
      style: selectedVoice
    });
  };
  const handleCancel = () => {
    setIsGenerationPending(false);
    setHasCancelled(true);
    UsageTracker.track('audioNarration', {
      action: 'cancel-generation'
    });
  };
  const handleClose = () => {
    if (isSaveToFilesPending) {
      return;
    }
    dispatch(closeAudioNarrationPanel);
    UsageTracker.track('audioNarration', {
      action: 'close-panel'
    });
  };
  const handleConfirmClose = () => {
    if (hasTranscriptChanged || audioUrl || isGenerationPending) {
      setIsUnsavedChangesOpen(true);
      UsageTracker.track('audioNarration', {
        action: audioUrl || isGenerationPending ? 'show-unsaved-narration-warning' : 'show-unsaved-transcript-warning'
      });
      return;
    }
    handleClose();
  };
  return /*#__PURE__*/_jsxs(UIModalPanel, {
    width: 600,
    "data-test-id": "narration-panel",
    onEsc: handleConfirmClose,
    onOpenComplete: () => setIsPanelOpenComplete(true),
    children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
      children: [/*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "audioNarration.panel.header"
        })
      }), /*#__PURE__*/_jsx(UIDialogCloseButton, {
        disabled: isSaveToFilesPending,
        "data-test-id": "audio-preview-close",
        onClick: handleConfirmClose
      })]
    }), /*#__PURE__*/_jsx(View, {
      position: "relative",
      children: isGenerationPending && /*#__PURE__*/_jsx(UIAbstractProgress, {
        incrementFactor: 0.025,
        maximum: 95,
        render: ({
          value,
          done
        }) => /*#__PURE__*/_jsx(UINanoProgress, {
          value: value,
          animateOnComplete: done
        })
      })
    }), /*#__PURE__*/_jsxs(DialogBody, {
      children: [/*#__PURE__*/_jsx(UIMediaObject, {
        className: "p-bottom-4",
        itemRight: /*#__PURE__*/_jsx(AIBadge, {}),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: textToAudioFileId ? 'audioNarration.panel.existingFile' : 'audioNarration.panel.description'
        })
      }), /*#__PURE__*/_jsx(FormControl, {
        error: isOverCharLimit,
        validationMessage: isOverCharLimit ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "audioNarration.panel.transcript.errors.maxLength"
        }) : null,
        children: /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsxs(Flex, {
            justifyContent: "space-between",
            inlineSize: "100%",
            alignItems: "center",
            children: [/*#__PURE__*/_jsx(FormLabel, {
              htmlFor: "audio-transcript",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "audioNarration.panel.transcript.label"
              })
            }), hasChangedFromPostBody && !isGenerationPending && /*#__PURE__*/_jsx(Small, {
              children: /*#__PURE__*/_jsx(UILink, {
                onClick: handleReset,
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "audioNarration.panel.reset"
                })
              })
            })]
          }), /*#__PURE__*/_jsx(UITextArea, {
            id: "audio-transcript",
            inputRef: textAreaRef,
            readOnly: isGenerationPending,
            value: inputText,
            onChange: e => setInputText(e.target.value),
            "data-test-id": "audio-transcript",
            rows: 13
          })]
        })
      }), /*#__PURE__*/_jsx(UIFormControl, {
        className: "m-top-4",
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "audioNarration.panel.voice.label"
        }),
        help: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "audioNarration.panel.voice.helpText"
        }),
        tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "audioNarration.panel.voice.aiDisclaimer"
        }),
        children: /*#__PURE__*/_jsx(VoiceSelectWithPreview, {
          disabled: isGenerationPending,
          showTags: true,
          value: voice,
          onChange: handleChangeVoice,
          onPreviewVoice: selectedVoice => {
            UsageTracker.track('audioNarration', {
              action: 'preview-voice',
              style: selectedVoice
            });
          },
          "data-test-id": "voice-select"
        })
      }), /*#__PURE__*/_jsx(AudioPreviewField, {
        audioUrl: audioUrl,
        file: file,
        hasChangedSinceLastGeneration: hasTranscriptChanged || hasVoiceChanged,
        isGenerativeAiEnabled: isGenerativeAiEnabled,
        isFileLoading: isFileLoading,
        isGenerationPending: isGenerationPending,
        isInputTextEmpty: isInputTextEmpty,
        isOverCharLimit: isOverCharLimit,
        isPanelOpenComplete: isPanelOpenComplete,
        isSaveToFilesPending: isSaveToFilesPending,
        onClickGenerate: () => void handleClickGenerate(),
        onRemove: () => void handleRemove()
      })]
    }), /*#__PURE__*/_jsx(AudioPanelFooter, {
      audioUrl: audioUrl,
      executionId: executionId,
      hasBlogAudioModule: hasBlogAudioModule,
      isGenerativeAiEnabled: isGenerativeAiEnabled,
      isFileFetchFailed: isFileFetchFailed,
      isGenerationPending: isGenerationPending,
      isInputTextEmpty: isInputTextEmpty,
      isOverCharLimit: isOverCharLimit,
      isRemovePending: isRemovePending,
      isSaveToFilesPending: isSaveToFilesPending,
      onAddToPost: handleAddToPost,
      onCancel: handleCancel,
      onClickGenerate: handleClickGenerate,
      onClose: handleConfirmClose,
      onRemove: handleRemove,
      textToAudioFileId: textToAudioFileId
    }), isUnsavedChangesOpen && /*#__PURE__*/_jsx(UnsavedTranscriptChanges, {
      hasGeneratedAudio: Boolean(audioUrl),
      onClose: handleClose,
      onCancel: () => setIsUnsavedChangesOpen(false)
    })]
  });
};
const WrappedPanel = withEditorActionsAndEvents(AudioPreviewPanel);
const TranscriptLoadingWrapper = props => {
  const dispatch = useDispatch();
  const contentId = useSelector(getContentModelId);
  const textToAudioFileId = useSelector(getContentModelTextToAudioFileId);
  const hasFetchedTranscript = useSelector(selectHasFetchedTranscript);
  useEffect(() => {
    if (textToAudioFileId && !hasFetchedTranscript) {
      dispatch(fetchTranscript(contentId)).catch(err => console.error('Failed to fetch transcript', err));
    } else {
      dispatch(updateAudioNarrationState({
        hasFetchedTranscript: true
      }));
    }
  }, [dispatch, contentId, textToAudioFileId, hasFetchedTranscript]);
  if (textToAudioFileId && !hasFetchedTranscript) {
    return /*#__PURE__*/_jsx(UILoadingOverlay, {
      contextual: true
    });
  }
  return /*#__PURE__*/_jsx(WrappedPanel, Object.assign({}, props));
};
export default TranscriptLoadingWrapper;